import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, ToggleButtonGroup,
  ToggleButton, Select, MenuItem, FormControl, InputLabel, Tooltip,
  LinearProgress, useTheme,
} from '@mui/material';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Cell, Legend,
} from 'recharts';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon       from '@mui/icons-material/Error';
import SpeedIcon       from '@mui/icons-material/Speed';
import StorageIcon     from '@mui/icons-material/Storage';
import api from '../utils/api';

const STATUS_COLOR = { healthy: '#16a34a', degraded: '#f59e0b', offline: '#dc2626', unknown: '#6b778c' };
const fmtTime = iso => {
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const fmtShort = iso => {
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit' });
};

function UptimeBadge({ pct }) {
  if (pct === null) return <Chip label="No data" size="small" sx={{ fontSize: '0.7rem' }} />;
  const color = pct >= 99 ? '#16a34a' : pct >= 95 ? '#f59e0b' : '#dc2626';
  const bg    = pct >= 99 ? 'rgba(22,163,74,0.1)' : pct >= 95 ? 'rgba(245,158,11,0.1)' : 'rgba(220,38,38,0.1)';
  return (
    <Chip label={`${pct.toFixed(1)}%`} size="small"
      sx={{ fontSize: '0.72rem', fontWeight: 700, color, backgroundColor: bg, borderRadius: '6px', height: 22 }} />
  );
}

function StatCard({ label, value, sub, icon, color }) {
  const theme = useTheme();
  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid', borderColor: 'divider', flex: 1, minWidth: 150 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.8 }}>
            {label}
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, color: color || 'text.primary', lineHeight: 1 }}>
            {value ?? <CircularProgress size={20} />}
          </Typography>
          {sub && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{sub}</Typography>}
        </Box>
        <Box sx={{ color: color || 'primary.main', opacity: 0.7 }}>{icon}</Box>
      </Box>
    </Paper>
  );
}

const CUSTOM_TOOLTIP = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper elevation={3} sx={{ p: 1.5, borderRadius: '10px', fontSize: '0.78rem' }}>
      <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>{label}</Typography>
      {payload.map(p => (
        <Box key={p.name} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: p.color }} />
          <span>{p.name}: <strong>{typeof p.value === 'number' ? p.value.toFixed(1) : p.value}</strong>{p.unit || ''}</span>
        </Box>
      ))}
    </Paper>
  );
};

export default function Analytics() {
  const theme   = useTheme();
  const dark    = theme.palette.mode === 'dark';
  const grid    = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const txt     = theme.palette.text.secondary;

  const [period, setPeriod]         = useState('24h');
  const [summary, setSummary]       = useState(null);
  const [live, setLive]             = useState([]);
  const [selected, setSelected]     = useState(null); // tenant id for timeline
  const [timeline, setTimeline]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [tlLoading, setTlLoading]   = useState(false);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get(`/analytics/summary?period=${period}`),
        api.get('/analytics/live'),
      ]);
      setSummary(s.data);
      setLive(l.data);
      if (!selected && s.data.tenants?.length > 0) setSelected(s.data.tenants[0].id);
    } catch {}
    finally { setLoading(false); }
  }, [period]);

  const loadTimeline = useCallback(async () => {
    if (!selected) return;
    setTlLoading(true);
    try {
      const r = await api.get(`/analytics/timeline/${selected}?period=${period}`);
      setTimeline(r.data);
    } catch {}
    finally { setTlLoading(false); }
  }, [selected, period]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadTimeline(); }, [loadTimeline]);

  // Auto-refresh live every 30s
  useEffect(() => {
    const iv = setInterval(async () => {
      try { const r = await api.get('/analytics/live'); setLive(r.data); } catch {}
    }, 30000);
    return () => clearInterval(iv);
  }, []);

  const online  = live.filter(t => t.status === 'healthy').length;
  const offline = live.filter(t => t.status !== 'healthy' && t.status !== 'unknown').length;
  const selectedTenant = summary?.tenants?.find(t => t.id === selected);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Analytics</Typography>
          <Typography variant="body2" color="text.secondary">Uptime, response time, and incident history</Typography>
        </Box>
        <ToggleButtonGroup exclusive value={period} onChange={(_, v) => v && setPeriod(v)} size="small"
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 2 } }}>
          <ToggleButton value="24h">24h</ToggleButton>
          <ToggleButton value="7d">7 days</ToggleButton>
          <ToggleButton value="30d">30 days</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* ── Summary cards ── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard label="Overall Uptime" value={summary ? `${summary.overall_site_uptime ?? '—'}%` : null}
          icon={<CheckCircleIcon />} color={summary?.overall_site_uptime >= 99 ? '#16a34a' : summary?.overall_site_uptime >= 95 ? '#f59e0b' : '#dc2626'} />
        <StatCard label="Tenants Online" value={online} sub={`of ${live.length} active`}
          icon={<StorageIcon />} color="#16a34a" />
        <StatCard label="Tenants Offline" value={offline}
          icon={<ErrorIcon />} color={offline > 0 ? '#dc2626' : '#6b778c'} />
        <StatCard label="Incidents" value={summary?.incidents?.length ?? '—'} sub={`in last ${period}`}
          icon={<SpeedIcon />} color={summary?.incidents?.length > 0 ? '#f59e0b' : '#6b778c'} />
      </Box>

      {/* ── Live status strip ── */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Live Status</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {live.map(t => (
            <Tooltip key={t.id} title={
              <Box>
                <div><strong>{t.name}</strong></div>
                <div>Status: {t.status}</div>
                {t.site_ms && <div>Response: {t.site_ms}ms</div>}
                {t.checked_at && <div>Checked: {fmtTime(t.checked_at)}</div>}
              </Box>
            }>
              <Chip
                size="small"
                label={t.name}
                onClick={() => setSelected(t.id)}
                icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: STATUS_COLOR[t.status] || '#6b778c', ml: '8px !important' }} />}
                sx={{
                  cursor: 'pointer',
                  fontWeight: selected === t.id ? 700 : 500,
                  border: selected === t.id ? `1px solid ${STATUS_COLOR[t.status] || '#6b778c'}` : '1px solid transparent',
                  backgroundColor: selected === t.id ? `${STATUS_COLOR[t.status]}18` : 'action.hover',
                  '& .MuiChip-icon': { ml: 0 },
                }}
              />
            </Tooltip>
          ))}
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>

        {/* ── Uptime bar chart ── */}
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid', borderColor: 'divider', flex: 2, minWidth: 320 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={2}>Site Uptime by Tenant ({period})</Typography>
          {loading ? <CircularProgress size={24} /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={summary?.tenants || []} layout="vertical" margin={{ left: 0, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: txt }} tickFormatter={v => `${v}%`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: txt }} width={110} />
                <RTooltip content={<CUSTOM_TOOLTIP />} formatter={v => [`${v?.toFixed(1)}%`, 'Uptime']} />
                <Bar dataKey="site_uptime" name="Uptime" radius={[0, 4, 4, 0]}>
                  {summary?.tenants?.map(t => (
                    <Cell key={t.id} fill={t.site_uptime >= 99 ? '#16a34a' : t.site_uptime >= 95 ? '#f59e0b' : t.site_uptime === null ? '#9ca3af' : '#dc2626'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>

        {/* ── Uptime table ── */}
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid', borderColor: 'divider', flex: 1.5, minWidth: 280, overflow: 'hidden' }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Uptime Summary ({period})</Typography>
          {loading ? <CircularProgress size={24} /> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary' }}>Tenant</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary' }}>Site</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary' }}>DB1</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary' }}>Avg ms</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary?.tenants?.map(t => (
                  <TableRow key={t.id} hover sx={{ cursor: 'pointer', backgroundColor: selected === t.id ? 'action.selected' : 'inherit' }}
                    onClick={() => setSelected(t.id)}>
                    <TableCell sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 600 }}>{t.name}</TableCell>
                    <TableCell align="center" sx={{ py: 0.75 }}><UptimeBadge pct={t.site_uptime} /></TableCell>
                    <TableCell align="center" sx={{ py: 0.75 }}><UptimeBadge pct={t.db1_uptime} /></TableCell>
                    <TableCell align="right" sx={{ py: 0.75, fontSize: '0.78rem', color: 'text.secondary' }}>
                      {t.avg_ms ? `${t.avg_ms}ms` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>

      {/* ── Timeline for selected tenant ── */}
      {selected && (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid', borderColor: 'divider', mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {selectedTenant?.name} — Uptime Timeline
            </Typography>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Tenant</InputLabel>
              <Select value={selected} label="Tenant" onChange={e => setSelected(e.target.value)}>
                {summary?.tenants?.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          {tlLoading ? <LinearProgress sx={{ borderRadius: 1 }} /> : timeline?.buckets?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={timeline.buckets} margin={{ left: -10, right: 10 }}>
                <defs>
                  <linearGradient id="siteGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="db1Grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#16a34a" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: txt }} tickFormatter={fmtShort} interval="preserveStartEnd" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: txt }} tickFormatter={v => `${v}%`} />
                <RTooltip content={<CUSTOM_TOOLTIP />} labelFormatter={fmtShort} />
                <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                <Area type="monotone" dataKey="site_uptime" name="Site" stroke="#3b82f6" fill="url(#siteGrad)" strokeWidth={2} dot={false} unit="%" />
                <Area type="monotone" dataKey="db1_uptime"  name="DB1"  stroke="#16a34a" fill="url(#db1Grad)"  strokeWidth={2} dot={false} unit="%" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <Alert severity="info" sx={{ borderRadius: '10px' }}>No health check data yet for this period. Checks run every 2 minutes.</Alert>
          )}
        </Paper>
      )}

      {/* ── Response time chart ── */}
      {selected && timeline?.points?.length > 0 && (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid', borderColor: 'divider', mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={2}>
            {selectedTenant?.name} — Response Time (ms)
          </Typography>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={timeline.points} margin={{ left: -10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} />
              <XAxis dataKey="time" tick={{ fontSize: 10, fill: txt }} tickFormatter={fmtShort} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: txt }} unit="ms" />
              <RTooltip content={<CUSTOM_TOOLTIP />} labelFormatter={fmtTime} />
              <Line type="monotone" dataKey="site_ms" name="Response" stroke="#8b5cf6" strokeWidth={1.5}
                dot={false} unit="ms" connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* ── Incidents ── */}
      {summary?.incidents?.length > 0 && (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Incidents ({period})</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Tenant', 'Type', 'Started', 'Last Seen', 'Failures'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.incidents.map((inc, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ py: 0.75, fontWeight: 600, fontSize: '0.82rem' }}>{inc.tenant_name}</TableCell>
                  <TableCell sx={{ py: 0.75 }}>
                    <Chip label={inc.type.toUpperCase()} size="small"
                      sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700,
                        backgroundColor: inc.type === 'site' ? 'rgba(220,38,38,0.1)' : 'rgba(245,158,11,0.1)',
                        color: inc.type === 'site' ? '#dc2626' : '#f59e0b', borderRadius: '5px' }} />
                  </TableCell>
                  <TableCell sx={{ py: 0.75, fontSize: '0.78rem', color: 'text.secondary' }}>{fmtTime(inc.started_at)}</TableCell>
                  <TableCell sx={{ py: 0.75, fontSize: '0.78rem', color: 'text.secondary' }}>{fmtTime(inc.last_seen)}</TableCell>
                  <TableCell sx={{ py: 0.75, fontSize: '0.78rem' }}>{inc.consecutive_failures}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
